import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  DenseTable,
  estimateSuffix,
  defaultNumberFormat,
  type Column,
} from "../../components/index.ts";
import type { GatewayStageRow } from "./use-enforcement.ts";
import {
  deriveGatewayStageViews,
  GATEWAY_CODE_ORDER,
  quantityMeasure,
  quantityValue,
  gatewayStageLabel,
  gatewayCodeLabel,
  gatewayHref,
  type GatewayStageView,
} from "./vocab.ts";

/**
 * Gateway pipeline failures by stage and code (task 11.8; design 12.2 "Gateway pipeline failures by
 * stage and code", FR-5.3).
 *
 * THE ALWAYS-PRESENT-WITH-ZEROS GUARANTEE. The breakdown is reconciled against the frozen eight-stage
 * × five-code catalog by `deriveGatewayStageViews`, so this table ALWAYS renders one row per stage
 * (`parse` … `other`) with a cell for EVERY Gateway error code (`gateway_audit_failure` …`other`) —
 * each an explicit 0 when nothing failed there (not a hidden/dropped row or cell). The contract
 * already zero-fills the server payload to the catalog; the derivation keeps the full grid ordered
 * and complete. An all-zero grid is the legitimate "no Gateway pipeline failures" state shown as
 * zeros — a healthy READY view, NOT an empty/collapsed state (FR-5.3).
 *
 * Layout: one row per stage; a leading stage column (row header), a per-code column for each of the
 * five error codes, and a trailing per-stage total. Every number drills to the events behind it —
 * a per-code cell to `category=gateway&stage=…&code=…`, the total to `category=gateway&stage=…`
 * (design 12.3). Counts carry their exact/estimate/unverifiable confidence; a null count renders as
 * "n/a" (grey, never green — design 12.3). The whole table has a semantic caption and `scope`'d
 * headers so a reader navigates the grid by stage and code (design 12.4).
 */

export interface GatewayStageFailuresProps {
  readonly rows: readonly GatewayStageRow[];
}

export function GatewayStageFailures(props: GatewayStageFailuresProps): ReactNode {
  const navigate = useNavigate();
  // Catalog-complete rows: one per stage, each with a cell per code, zero-filled. Never dropped.
  const views = deriveGatewayStageViews(props.rows);
  const anyFailures = views.some(
    (s) => quantityValue(s.total) > 0 || s.cells.some((c) => quantityValue(c.count) > 0),
  );

  const codeColumns: readonly Column<GatewayStageView>[] = GATEWAY_CODE_ORDER.map((code, index) => ({
    key: `code-${code}`,
    header: gatewayCodeLabel(code),
    align: "right",
    cell: (s) => {
      const cell = s.cells[index];
      const count = cell?.count ?? s.cells.find((c) => c.code === code)?.count;
      if (count === undefined) return <span className="enf-faint">n/a</span>;
      const measure = quantityMeasure(count);
      const text =
        count.value === null ? "n/a" : `${defaultNumberFormat(count.value)}${estimateSuffix(measure)}`;
      return (
        <button
          type="button"
          className="enf-link enf-num"
          data-measure={measure}
          onClick={() => navigate(gatewayHref(s.stage, code))}
          aria-label={`Open the Gateway events for ${gatewayStageLabel(
            s.stage,
          ).toLowerCase()} ${gatewayCodeLabel(code).toLowerCase()}`}
        >
          {text}
        </button>
      );
    },
  }));

  const columns: readonly Column<GatewayStageView>[] = [
    {
      key: "stage",
      header: "Stage",
      isRowHeader: true,
      cell: (s) => <span className="enf-row-key">{gatewayStageLabel(s.stage)}</span>,
    },
    ...codeColumns,
    {
      key: "total",
      header: "Total",
      align: "right",
      cell: (s) => {
        const measure = quantityMeasure(s.total);
        const text =
          s.total.value === null
            ? "n/a"
            : `${defaultNumberFormat(s.total.value)}${estimateSuffix(measure)}`;
        return (
          <button
            type="button"
            className="enf-link enf-num enf-num--strong"
            data-measure={measure}
            onClick={() => navigate(gatewayHref(s.stage, null))}
            aria-label={`Open all Gateway failure events for ${gatewayStageLabel(
              s.stage,
            ).toLowerCase()}`}
          >
            {text}
          </button>
        );
      },
    },
  ];

  return (
    <section className="enf-section enf-gateway" aria-label="Gateway pipeline failures by stage and code">
      <div className="enf-section__head">
        <h2 className="enf-section__title">Gateway pipeline failures</h2>
        <span className="enf-section__note">
          {anyFailures
            ? "Pipeline failures were reported in this window."
            : "No pipeline failures. Every stage and code is shown with zero."}
        </span>
      </div>
      {/* `rows` is never empty here (one per catalog stage); `emptyMessage` is a safeguard only, the
          all-zero case renders real zero rows/cells, not this message. */}
      <div className="enf-gateway__scroll">
        <DenseTable
          caption="Gateway pipeline failures by stage and error code, one row per stage, one cell per code, zero when none occurred"
          columns={columns}
          rows={views}
          rowKey={(s) => s.stage}
          stickyHeader
          emptyMessage="The Gateway pipeline stages could not be loaded for this window."
        />
      </div>
    </section>
  );
}
